import { Injectable } from '@angular/core';
import { firstValueFrom, Observable, of } from 'rxjs';
import { WindowService } from 'shared/services/window/window.service';
import { BcConfigService } from 'common/config/provider';
import { GenesysCloudApiEndpoints, GenesysCloudConfigService } from 'common/genesys-cloud-config';
import { map, tap } from 'rxjs/operators';
import {
  AuthenticationResult,
  AuthenticationResultFailure,
  AuthenticationResultSuccess
} from 'common/auth/models/authentication-result';
import { GenesysUserPermissionService } from 'common/store/genesys-cloud/users/services/user-permission.service';
import { UserPermission } from 'common/store/genesys-cloud/users/models/user-permission.enum';

const OAUTH_PATH = '/oauth/authorize';
const LOGOUT_PATH = '/logout';

@Injectable({
  providedIn: 'root'
})
export class GenesysCloudAuthService {
  private readonly genesysCloudClientId: string;
  private isGenesysLoggedIn: boolean = false;

  constructor(private readonly config: BcConfigService,
              private readonly $window: WindowService,
              private readonly genesysCloudConfigService: GenesysCloudConfigService,
              private readonly genesysUserPermissionService: GenesysUserPermissionService) {
    this.genesysCloudClientId = this.config.current.genesysClientId;
  }

  init(): Observable<AuthenticationResult> {
    const fragment = this.$window.location.hash.substring(1);
    const params = new URLSearchParams(fragment);
    const state = params.get('state');
    let accessToken = params.get('access_token');
    let expiresAt;
    let genesysCloudApiEndpoints$: Observable<GenesysCloudApiEndpoints>;

    if (state) {
      params.delete('state');
      const reducedURL = decodeURIComponent(`${state}#${params.toString()}`);
      this.$window.location.replace(reducedURL);
    }

    if (accessToken) {
      const expiresIn = Number(params.get('expires_in'));
      expiresAt = Date.now() + expiresIn * 1000;
      genesysCloudApiEndpoints$ = this.genesysCloudConfigService.apiEndpoints$.pipe(
        tap((apiEndpoints) => {
          this.genesysCloudConfigService.saveConfigToStorage(accessToken, expiresAt, apiEndpoints);
        })
      );
    } else {
      const apiConfigFromStorage = this.genesysCloudConfigService.getConfigFromStorage();
      if (apiConfigFromStorage) {
        accessToken = apiConfigFromStorage.accessToken;
        expiresAt = apiConfigFromStorage.expiresAt;
        genesysCloudApiEndpoints$ = of(apiConfigFromStorage.apiEndpoints);
      }
    }

    if (!accessToken) {
      return this.genesysCloudConfigService.apiEndpoints$.pipe(
        map((apiEndpoints) => {
          this.isGenesysLoggedIn = false;
          return new AuthenticationResultFailure(this.generateLoginUrl(apiEndpoints));
        })
      );
    }

    return genesysCloudApiEndpoints$.pipe(
      map((genesysCloudApiEndpoints) => {
        this.emitSubjectValue(accessToken, expiresAt, genesysCloudApiEndpoints);
        this.isGenesysLoggedIn = true;
        return new AuthenticationResultSuccess();
      })
    );
  }

  canActivateRoute(): boolean {
    return this.isLoggedIn();
  }

  isLoggedIn(): boolean {
    return this.isGenesysLoggedIn;
  }

  async logout(): Promise<string> {
    this.isGenesysLoggedIn = false;
    this.genesysCloudConfigService.clearConfigFromStorage();
    return firstValueFrom(this.genesysCloudConfigService.apiEndpoints$.pipe(
      map(apiEndpoints => this.generateLogoutUrl(apiEndpoints))
    ));
  }

  async getAccessToken(): Promise<string> {
    return firstValueFrom(this.genesysCloudConfigService.apiConfig$.pipe(
      map(config => config.accessToken)
    ));
  }

  async isDigitalDesktopUiAuthorized(): Promise<boolean> {
    return firstValueFrom(this.genesysUserPermissionService.hasPermission$(UserPermission.UiDigitalDesktopView));
  }

  private generateLoginUrl(apiEndpoints: GenesysCloudApiEndpoints): string {
    const authUrl = this.generateAuthUrl(apiEndpoints, OAUTH_PATH);
    return `${authUrl}&response_type=token`;
  }

  private generateLogoutUrl(apiEndpoints: GenesysCloudApiEndpoints): string {
    return this.generateAuthUrl(apiEndpoints, LOGOUT_PATH);
  }

  private generateAuthUrl(apiEndpoints: GenesysCloudApiEndpoints, path: string): string {
    const authUri = apiEndpoints.authUri;
    const { protocol, host, pathname } = this.$window.location;
    const basePath = pathname ? this.getBasePath(pathname) : '';
    const baseUrl = `${protocol}//${host}${basePath || '/'}`;
    const returnUrl = encodeURIComponent(baseUrl);
    const stateUrl = encodeURIComponent(`${protocol}//${host}${pathname || ''}`);
    return `${authUri}${path}?client_id=${this.genesysCloudClientId}&redirect_uri=${returnUrl}&state=${stateUrl}`;
  }

  private emitSubjectValue(accessToken: string, expiresAt: number, genesysCloudApiEndpoints: GenesysCloudApiEndpoints): void {
    this.genesysCloudConfigService.emitApiConfig({
      accessToken: accessToken,
      expiresAt: expiresAt,
      apiEndpoints: genesysCloudApiEndpoints
    });
  }

  private getBasePath(pathname: string) {
    const pathSegments = pathname.split('/');
    return `/${pathSegments[1]}`;
  }
}
